import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card, CardContent, CardHeader, CardTitle
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Upload, Search, Video, FileText, Clock, Loader2, ListVideo, Plus, Play,
  Trash2, Info, X, CheckSquare, Square, MoreVertical, Star, ArrowLeft, ChevronRight, LayoutGrid, Grid
} from 'lucide-react';
import SimpleVideoPlayer from '../components/SimpleVideoPlayer';
import { cn } from '../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { useTeam } from '../context/TeamContext';
import { videosAPI, playlistsAPI, BACKEND_URL } from '../services/api';
import TeamGate from '../components/TeamGate';
import { useAuth } from '../context/AuthContext';
import CreatePlaylistModal from '../components/CreatePlaylistModal';

const Videos = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { refreshTeamStatus } = useTeam();
  const [activeTab, setActiveTab] = useState('videos');
  const [searchQuery, setSearchQuery] = useState('');
  const [videos, setVideos] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // New State for enhancements
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null); // specific video or null for bulk
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  // Add to Playlist state
  const [addToPlaylistOpen, setAddToPlaylistOpen] = useState(false);
  const [addToPlaylistVideo, setAddToPlaylistVideo] = useState(null);
  const [allPlaylists, setAllPlaylists] = useState([]);
  const [addingToPlaylist, setAddingToPlaylist] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (activeTab === 'videos') {
          const response = await videosAPI.list();
          setVideos(response.data);
        } else {
          const response = await playlistsAPI.list();
          setPlaylists(response.data);
        }
      } catch (error) {
        console.error(`Failed to fetch ${activeTab}:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Clear selection when tab changes
    setSelectedVideos(new Set());
  }, [activeTab]);

  const fetchPlaylists = async () => {
    try {
      setLoading(true);
      const response = await playlistsAPI.list();
      setPlaylists(response.data);
    } catch (error) {
      console.error('Failed to fetch playlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = videos.filter(video =>
    video.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    video.game_info?.opponent?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPlaylists = playlists.filter(playlist =>
    playlist.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // --- Selection Logic ---
  const toggleSelection = (videoId, e) => {
    if (e) e.stopPropagation();
    const newSelection = new Set(selectedVideos);
    if (newSelection.has(videoId)) {
      newSelection.delete(videoId);
    } else {
      newSelection.add(videoId);
    }
    setSelectedVideos(newSelection);
  };

  const selectAll = () => {
    if (selectedVideos.size === filteredVideos.length) {
      setSelectedVideos(new Set());
    } else {
      setSelectedVideos(new Set(filteredVideos.map(v => v.id)));
    }
  };

  // --- Delete Logic ---
  const handleDeleteClick = (video, e) => {
    if (e) e.stopPropagation();
    setVideoToDelete(video);
    setIsBulkDelete(false);
    setDeleteDialogOpen(true);
  };

  const handleBulkDeleteClick = () => {
    setIsBulkDelete(true);
    setVideoToDelete(null);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (isBulkDelete) {
        await videosAPI.bulkDelete(Array.from(selectedVideos));
        setVideos(videos.filter(v => !selectedVideos.has(v.id)));
        setSelectedVideos(new Set());
      } else if (videoToDelete) {
        await videosAPI.delete(videoToDelete.id);
        setVideos(videos.filter(v => v.id !== videoToDelete.id));
      }
    } catch (error) {
      console.error('Delete failed:', error);
      // Could show toast error here
    } finally {
      setDeleteDialogOpen(false);
      setVideoToDelete(null);
    }
  };

  // --- Usage Info Logic ---
  const handleShowUsage = async (video, e) => {
    if (e) e.stopPropagation();
    setUsageDialogOpen(true);
    setUsageLoading(true);
    try {
      const response = await videosAPI.getUsage(video.id);
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to get usage:', error);
    } finally {
      setUsageLoading(false);
    }
  };

  // --- Star Logic ---
  const handleToggleStar = async (video, e) => {
    if (e) e.stopPropagation();
    try {
      await videosAPI.toggleStar(video.id);
      // Update local state
      setVideos(videos.map(v =>
        v.id === video.id ? { ...v, is_starred: !v.is_starred } : v
      ));
    } catch (error) {
      console.error('Failed to toggle star:', error);
    }
  };

  // --- Playback Logic ---
  const handlePlayVideo = (video) => {
    setCurrentVideo(video);
    setPlayerOpen(true);
  };

  // --- Add to Playlist Logic ---
  const handleAddToPlaylist = async (video, e) => {
    if (e) e.stopPropagation();
    setAddToPlaylistVideo(video);
    setAddToPlaylistOpen(true);
    try {
      const response = await playlistsAPI.list();
      setAllPlaylists(response.data);
    } catch (error) {
      console.error('Failed to fetch playlists:', error);
    }
  };

  const confirmAddToPlaylist = async (playlistId) => {
    if (!addToPlaylistVideo) return;
    setAddingToPlaylist(playlistId);
    try {
      await playlistsAPI.addVideo(playlistId, addToPlaylistVideo.id);
      setAddToPlaylistOpen(false);
      setAddToPlaylistVideo(null);
    } catch (error) {
      console.error('Failed to add video to playlist:', error);
    } finally {
      setAddingToPlaylist(null);
    }
  };

  const canDelete = user?.role === 'coach' || user?.role === 'admin';

  // Helper component for rendering video cards
  const VideoCard = ({ video }) => (
    <div
      onClick={() => handlePlayVideo(video)}
      className={cn(
        "bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 rounded-[2rem] overflow-hidden group relative transition-all duration-300 shadow-xl active:scale-[0.98]",
        selectedVideos.has(video.id) && "ring-2 ring-snapp-blue border-transparent"
      )}
    >
      <div className="relative aspect-video overflow-hidden bg-black">
        {video.thumbnail_url ? (
          <img
            src={video.thumbnail_url?.startsWith('http') ? video.thumbnail_url : `${BACKEND_URL}${video.thumbnail_url}`}
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#1a1a1b]">
            <Play className="w-12 h-12 text-white/10" />
          </div>
        )}

        {/* Play Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
          <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 group-hover:scale-110 transition-transform">
            <Play className="w-5 h-5 text-white fill-current ml-0.5" />
          </div>
        </div>

        {/* Duration Badge */}
        <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-md text-white text-[10px] font-black px-2.5 py-1 rounded-lg border border-white/10 uppercase tracking-widest tabular-nums">
          {formatDuration(video.duration_seconds)}
        </div>

        {/* Selection Checkbox (Visible on hover or selected) */}
        {canDelete && (
          <div
            className={cn(
              "absolute top-3 left-3 z-10 transition-all duration-300",
              selectedVideos.has(video.id) ? "opacity-100 scale-100" : "opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100"
            )}
            onClick={(e) => toggleSelection(video.id, e)}
          >
            <div className={cn(
              "w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all",
              selectedVideos.has(video.id) ? "bg-snapp-blue border-snapp-blue shadow-lg shadow-blue-500/30" : "bg-black/50 border-white/30"
            )}>
              {selectedVideos.has(video.id) && <CheckSquare className="w-4 h-4 text-white" />}
            </div>
          </div>
        )}

        {/* Star Badge */}
        {video.is_starred && (
          <div className="absolute top-3 right-3 bg-amber-500 p-1.5 rounded-lg border-2 border-[#1a1a1b] shadow-xl">
            <Star className="w-3 h-3 text-white fill-current" />
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div>
          <h3 className="text-white font-black text-sm tracking-tight leading-tight line-clamp-1">{video.title || t('videos.untitled')}</h3>
          {video.game_info && (
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-1">
              {t('videos.vs')} {video.game_info.opponent}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between pt-1 border-t border-white/5">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-gray-600" />
            <span className="text-[10px] font-black text-gray-600 uppercase tracking-widest tabular-nums">
              {new Date(video.created_at).toLocaleDateString()}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => handleAddToPlaylist(video, e)}
              className="p-2 hover:bg-snapp-blue/10 text-gray-500 hover:text-snapp-blue rounded-lg transition-colors"
              title="Playliste Ekle"
            >
              <ListVideo className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => handleShowUsage(video, e)}
              className="p-2 hover:bg-white/5 text-gray-500 hover:text-white rounded-lg transition-colors"
            >
              <Info className="w-4 h-4" />
            </button>
            {canDelete && (
              <button
                onClick={(e) => handleToggleStar(video, e)}
                className={cn("p-2 rounded-lg transition-colors", video.is_starred ? "text-amber-500 hover:bg-amber-500/10" : "text-gray-500 hover:text-white hover:bg-white/5")}
              >
                <Star className={cn("w-4 h-4", video.is_starred && "fill-current")} />
              </button>
            )}
            {canDelete && (
              <button
                onClick={(e) => handleDeleteClick(video, e)}
                className="p-2 hover:bg-red-500/10 text-gray-500 hover:text-red-500 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <TeamGate title="Videos">
      <div className="min-h-screen bg-[#0a0a0b] text-white pb-24 animate-in fade-in duration-500">
        {/* Mobile Sticky Header */}
        <div className="lg:hidden p-4 bg-[#0a0a0b]/80 backdrop-blur-xl sticky top-0 z-[100] border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-black text-white tracking-tight uppercase">
              {activeTab === 'videos' ? t('videos.title') : t('playlists.title')}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'videos' && selectedVideos.size > 0 && (
              <button
                onClick={handleBulkDeleteClick}
                className="p-2 bg-red-500 text-white rounded-xl active:scale-90"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <div className="px-3 py-1 bg-snapp-purple/10 border border-snapp-purple/20 rounded-full">
              <span className="text-[10px] font-black text-snapp-purple uppercase tracking-widest">
                {activeTab === 'videos' ? videos.length : playlists.length}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 lg:p-8 space-y-8">
          {/* Desktop Header */}
          <div className="hidden lg:flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-black text-white uppercase tracking-tight">
                {activeTab === 'videos' ? t('videos.title') : t('playlists.title')}
              </h1>
              <p className="text-gray-400 font-medium mt-1">
                {activeTab === 'videos' ? t('videos.subtitle') : t('playlists.subtitle')}
              </p>
            </div>
            {user?.role === 'coach' && activeTab === 'videos' && (
              <div className="flex gap-3">
                {selectedVideos.size > 0 && (
                  <button
                    onClick={handleBulkDeleteClick}
                    className="bg-red-500 hover:bg-red-600 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg shadow-red-500/20 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> {t('videos.deleteSelected')} ({selectedVideos.size})
                  </button>
                )}
                <button className="bg-snapp-blue hover:bg-snapp-blue/90 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg shadow-blue-500/20 flex items-center gap-2">
                  <Upload className="w-4 h-4" /> {t('videos.upload')}
                </button>
              </div>
            )}
            {activeTab === 'playlists' && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-snapp-blue hover:bg-snapp-blue/90 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg shadow-blue-500/20 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> {t('playlists.create')}
              </button>
            )}
          </div>

          {/* Premium Tab Bar */}
          <div className="bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 p-1.5 rounded-[1.5rem] flex gap-2">
            <button
              onClick={() => setActiveTab('videos')}
              className={cn(
                "flex-1 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2",
                activeTab === 'videos' ? "bg-snapp-purple text-white shadow-xl shadow-purple-500/20" : "text-gray-500 hover:text-white hover:bg-white/5"
              )}
            >
              <Video className="w-3.5 h-3.5" />
              {t('nav.videos')}
            </button>
            <button
              onClick={() => setActiveTab('playlists')}
              className={cn(
                "flex-1 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2",
                activeTab === 'playlists' ? "bg-snapp-purple text-white shadow-xl shadow-purple-500/20" : "text-gray-500 hover:text-white hover:bg-white/5"
              )}
            >
              <ListVideo className="w-3.5 h-3.5" />
              {t('playlists.title')}
            </button>
          </div>

          {/* Search & Selection Bar */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-snapp-purple transition-colors" />
              <input
                type="text"
                placeholder={t('common.search') + "..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#1a1a1b] border border-white/5 focus:border-snapp-purple/30 rounded-[1.5rem] pl-12 pr-4 py-3.5 text-sm text-white placeholder:text-gray-600 outline-none transition-all shadow-xl font-medium"
              />
            </div>
            {activeTab === 'videos' && canDelete && (
              <button
                onClick={selectAll}
                className="px-6 py-3.5 bg-[#1a1a1b] border border-white/5 rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-white transition-all flex items-center justify-center gap-2 active:scale-95"
              >
                <div className={cn(
                  "w-4 h-4 rounded-md border-2 transition-all flex items-center justify-center",
                  filteredVideos.length > 0 && selectedVideos.size === filteredVideos.length ? "bg-snapp-blue border-snapp-blue" : "border-white/30"
                )}>
                  {filteredVideos.length > 0 && selectedVideos.size === filteredVideos.length && <CheckSquare className="w-3 h-3 text-white" />}
                </div>
                {t('common.selectAll') || 'Tümünü Seç'}
              </button>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-2 border-snapp-blue border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : activeTab === 'videos' ? (
            <div className="space-y-12">
              {/* Starred Videos Section */}
              {filteredVideos.some(v => v.is_starred) && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 px-2">
                    <div className="p-2 bg-amber-500/10 rounded-xl">
                      <Star className="w-5 h-5 text-amber-500 fill-current" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('videos.featuredVideos')}</h3>
                      <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-0.5">Top Highlights</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredVideos.filter(v => v.is_starred).map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                </div>
              )}

              {/* All Videos Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 px-2">
                  <div className="p-2 bg-snapp-purple/10 rounded-xl">
                    <Video className="w-5 h-5 text-snapp-purple" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('videos.allVideos')}</h3>
                    <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-0.5">Latest Uploads</p>
                  </div>
                </div>
                {filteredVideos.length === 0 ? (
                  <div className="text-center py-20 bg-[#1a1a1b]/30 rounded-[2.5rem] border border-white/5 border-dashed">
                    <Video className="w-16 h-16 text-white/5 mx-auto mb-4" />
                    <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">{t('videos.noVideos')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredVideos.filter(v => !v.is_starred).map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredPlaylists.map((playlist) => (
                <div
                  key={playlist.id}
                  onClick={() => navigate(`/playlists/${playlist.id}`)}
                  className="bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 rounded-[2rem] p-6 shadow-xl relative overflow-hidden group transition-all duration-300 active:scale-[0.98] cursor-pointer"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="p-3 bg-snapp-blue/10 rounded-2xl">
                        <ListVideo className="w-6 h-6 text-snapp-blue" />
                      </div>
                      <div className="px-3 py-1 bg-white/5 rounded-full">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest tabular-nums">{playlist.video_count || 0} Clips</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-white tracking-tight leading-tight group-hover:text-snapp-blue transition-colors">{playlist.name}</h3>
                      <p className="text-[10px] font-black text-gray-600 uppercase tracking-widest mt-1">Created {new Date(playlist.created_at).toLocaleDateString()}</p>
                    </div>
                    <button className="w-full bg-white/5 hover:bg-snapp-blue text-gray-400 hover:text-white h-12 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2">
                      <Play className="w-3.5 h-3.5 fill-current" />
                      {t('playlists.play')}
                    </button>
                  </div>
                </div>
              ))}
              {filteredPlaylists.length === 0 && (
                <div className="col-span-full text-center py-20 bg-[#1a1a1b]/30 rounded-[2.5rem] border border-white/5 border-dashed">
                  <ListVideo className="w-16 h-16 text-white/5 mx-auto mb-4" />
                  <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">{t('playlists.noPlaylists')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Floating Action Button for Mobile Coaches */}
        {user?.role === 'coach' && (
          <button
            onClick={() => activeTab === 'videos' ? null : setIsCreateModalOpen(true)}
            className="lg:hidden fixed bottom-28 right-6 w-14 h-14 bg-snapp-blue text-white rounded-2xl shadow-2xl flex items-center justify-center transition-all active:scale-90 z-[101]"
          >
            {activeTab === 'videos' ? <Upload className="w-8 h-8" /> : <Plus className="w-8 h-8" />}
          </button>
        )}

        <CreatePlaylistModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={fetchPlaylists}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-[#1a1a1b] border-gray-800 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('common.confirmTitle') || 'Emin misiniz?'}</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                {isBulkDelete
                  ? t('videos.bulkDeleteConfirmDesc', { count: selectedVideos.size })
                  : t('videos.deleteConfirmDesc')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-gray-600 text-white hover:bg-gray-800 hover:text-white">{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">{t('common.delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Usage Info Dialog */}
        <Dialog open={usageDialogOpen} onOpenChange={setUsageDialogOpen}>
          <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white max-w-md" aria-describedby="usage-desc">
            <DialogHeader>
              <DialogTitle>{t('videos.usageTitle')}</DialogTitle>
              <DialogDescription id="usage-desc" className="sr-only">Video usage information</DialogDescription>
            </DialogHeader>
            {usageLoading ? (
              <div className="flex justify-center p-4">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : usageData ? (
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-blue-400">
                    <ListVideo className="w-4 h-4" /> {t('videos.inPlaylists')}
                  </h4>
                  {usageData.playlists.length > 0 ? (
                    <ul className="list-disc list-inside text-sm text-gray-300">
                      {usageData.playlists.map(p => (
                        <li key={p.id}>{p.name}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 italic">{t('videos.noPlaylistUsage')}</p>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-green-400">
                    <Video className="w-4 h-4" /> {t('videos.inSessions')}
                  </h4>
                  {usageData.sessions.length > 0 ? (
                    <ul className="list-disc list-inside text-sm text-gray-300">
                      {usageData.sessions.map(s => (
                        <li key={s.id}>{s.name} ({new Date(s.date).toLocaleDateString()})</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 italic">{t('videos.noSessionUsage')}</p>
                  )}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Video Player Dialog */}
        <Dialog open={playerOpen} onOpenChange={setPlayerOpen}>
          <DialogContent className="bg-black border-gray-800 text-white max-w-4xl p-0 overflow-hidden" aria-describedby="video-player-desc">
            <DialogHeader className="p-4 bg-[#1a1a1b] absolute top-0 w-full z-10 bg-opacity-80 backdrop-blur-sm">
              <DialogTitle className="flex items-center justify-between">
                <span>{currentVideo?.title}</span>
              </DialogTitle>
              <DialogDescription id="video-player-desc" className="sr-only">
                Video player for {currentVideo?.title}
              </DialogDescription>
            </DialogHeader>
            <div className="relative bg-black pt-14 flex justify-center items-center h-[80vh]">
              {currentVideo && (
                <SimpleVideoPlayer
                  src={(() => {
                    const rawUrl = currentVideo.video_url || currentVideo.url;
                    const urlWithToken = `${rawUrl}?token=${localStorage.getItem('token')}`;
                    if (urlWithToken.startsWith('http')) return urlWithToken;
                    return `${BACKEND_URL}${urlWithToken}`;
                  })()}
                  className="w-full h-full"
                  autoPlay={true}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Add to Playlist Dialog */}
        <Dialog open={addToPlaylistOpen} onOpenChange={setAddToPlaylistOpen}>
          <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white max-w-md" aria-describedby="add-playlist-desc">
            <DialogHeader>
              <DialogTitle>Playliste Ekle</DialogTitle>
              <DialogDescription id="add-playlist-desc" className="text-gray-400 text-sm">
                Videonun ekleneceği playlisti seçin.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {allPlaylists.length === 0 ? (
                <p className="text-gray-500 text-center py-8 text-sm">Henüz playlist oluşturulmamış.</p>
              ) : (
                allPlaylists.map(pl => (
                  <button
                    key={pl.id}
                    onClick={() => confirmAddToPlaylist(pl.id)}
                    disabled={addingToPlaylist === pl.id}
                    className="w-full flex items-center gap-3 p-3 bg-[#0a0a0b] hover:bg-white/5 rounded-xl border border-white/5 transition-all text-left group"
                  >
                    <div className="p-2 bg-snapp-blue/10 rounded-lg">
                      <ListVideo className="w-5 h-5 text-snapp-blue" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold text-white truncate group-hover:text-snapp-blue transition-colors">{pl.name}</h4>
                      <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">{pl.video_count || 0} video</p>
                    </div>
                    {addingToPlaylist === pl.id ? (
                      <Loader2 className="w-4 h-4 animate-spin text-snapp-blue" />
                    ) : (
                      <Plus className="w-4 h-4 text-gray-500 group-hover:text-snapp-blue transition-colors" />
                    )}
                  </button>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </TeamGate >
  );
};

export default Videos;
